import { createContext, useContext, useEffect } from 'react';

export interface BreadcrumbItem {
  readonly label: string;
  /** Omit on the trailing crumb. */
  readonly to?: string;
}

/**
 * App-shell context surfaced to every route. Lets descendants trigger
 * global affordances (palette) and push richer state into the topbar
 * (breadcrumb trail) without prop drilling.
 */
export interface ShellContextValue {
  readonly openQuickSearch: () => void;
  readonly breadcrumbs: readonly BreadcrumbItem[] | null;
  readonly setBreadcrumbs: (items: readonly BreadcrumbItem[] | null) => void;
}

export const ShellContext = createContext<ShellContextValue | null>(null);

export function useShell(): ShellContextValue {
  const value = useContext(ShellContext);
  if (value === null) {
    throw new Error('useShell() must be used inside <ShellContext.Provider>.');
  }
  return value;
}

/**
 * Pushes a breadcrumb trail for the duration of the calling component.
 * Cleared on unmount so navigating away falls back to the URL-derived
 * default. Trail is JSON-hashed so call sites don't have to wrap their
 * array in `useMemo`.
 */
export function useSetBreadcrumbs(items: readonly BreadcrumbItem[] | null): void {
  const { setBreadcrumbs } = useShell();
  const dep = items === null ? null : JSON.stringify(items);
  useEffect(() => {
    const resolved =
      dep === null ? null : (JSON.parse(dep) as readonly BreadcrumbItem[]);
    setBreadcrumbs(resolved);
    return () => {
      setBreadcrumbs(null);
    };
  }, [setBreadcrumbs, dep]);
}
